import React, { useState } from 'react';
import KeywordFooter from '../components/KeywordFooter';

const PRIVACY_EMAIL = 'jimmy.lagattuta@gmail.com';

const Privacy = () => {
  const effectiveDate = 'May 13, 2026';
  const [copiedEmail, setCopiedEmail] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(PRIVACY_EMAIL);
      setCopiedEmail(true);

      window.setTimeout(() => {
        setCopiedEmail(false);
      }, 2200);
    } catch (error) {
      console.error('Unable to copy privacy email:', error);
      setCopiedEmail(false);
    }
  };

  return (
    <>
      <main className="min-h-screen bg-slate-950 px-6 py-16 text-white">
        <section className="mx-auto max-w-4xl rounded-3xl border border-cyan-300/20 bg-slate-900/70 p-6 shadow-2xl shadow-cyan-950/30 sm:p-8">
          <p className="text-sm font-bold uppercase tracking-[0.3em] text-cyan-300">
            SubtitleTsunami
          </p>

          <h1 className="mt-3 text-3xl font-black text-white sm:text-4xl">
            Privacy Policy
          </h1>

          <p className="mt-3 text-sm font-bold text-slate-400">
            Effective Date: {effectiveDate}
          </p>

          <p className="mt-6 text-sm leading-7 text-slate-300">
            This Privacy Policy explains how SubtitleTsunami collects, uses,
            stores, and protects information when you use our website, account
            features, video upload tools, subtitle processing tools, payment
            features, previews, downloads, and related services.
          </p>

          <p className="mt-4 text-sm leading-7 text-slate-300">
            By using SubtitleTsunami, you agree to the collection and use of
            information described in this Privacy Policy.
          </p>

          <div className="mt-8 space-y-8">
            <section>
              <h2 className="text-xl font-black text-white">
                1. Information We Collect
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may collect information you provide directly, information
                generated through your use of the service, and technical
                information needed to operate, secure, and improve
                SubtitleTsunami.
              </p>

              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-300">
                <li>
                  Account information, such as your name, email address, login
                  status, and account settings.
                </li>
                <li>
                  Uploaded files, including videos submitted for subtitle
                  processing.
                </li>
                <li>
                  Processing data, including generated subtitles, translated
                  subtitle text, download tokens, processing status, file names,
                  video duration, and related workflow data.
                </li>
                <li>
                  Usage information, such as upload activity, plan limits,
                  processing events, feature usage, errors, and service logs.
                </li>
                <li>
                  Payment-related information handled through our payment
                  provider, such as subscription status, billing status, payment
                  method identifiers, transaction records, and plan details.
                </li>
                <li>
                  Technical information, such as IP address, browser type,
                  device type, operating system, referring pages, timestamps,
                  cookies, and similar diagnostic data.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                2. Uploaded Videos and Processed Files
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami allows users to upload videos for automated
                transcription, translation, subtitle generation, preview, and
                download. Uploaded videos and processed files may be temporarily
                stored on SubtitleTsunami servers, cloud storage providers, or
                third-party infrastructure providers so the service can process
                and deliver the final output.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Uploaded and processed files are intended to be temporary.
                Download links, preview links, generated subtitle files, and
                processed video files may expire or be deleted after a limited
                period for storage, security, cost control, and system
                optimization purposes.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                You are responsible for downloading and saving any processed
                files you want to keep before they expire.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                3. Copyright and Upload Confirmation Data
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami is intended for videos you own or have
                permission to use. When you upload a video, we may record that
                you confirmed you have the necessary rights to upload, subtitle,
                translate, process, preview, and download that content.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may store rights-confirmation data such as whether the
                confirmation was checked, the time of confirmation, the
                associated account, the original file name, the video duration,
                and the IP address associated with the request.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                This information helps us operate the service responsibly,
                investigate misuse, respond to copyright concerns, enforce our
                Terms of Service, and protect SubtitleTsunami, users, payment
                providers, and infrastructure providers.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                4. How We Use Information
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We use information to provide, maintain, secure, and improve
                SubtitleTsunami.
              </p>

              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-300">
                <li>To create and manage user accounts.</li>
                <li>
                  To upload, store, process, subtitle, translate, preview, and
                  deliver video files.
                </li>
                <li>To generate download links and temporary access tokens.</li>
                <li>
                  To track usage limits, plan limits, subscription access, and
                  service availability.
                </li>
                <li>
                  To process payments, manage subscriptions, and respond to
                  billing questions.
                </li>
                <li>
                  To troubleshoot errors, improve performance, prevent abuse,
                  and secure the platform.
                </li>
                <li>
                  To respond to support requests, copyright complaints, legal
                  requests, and policy violations.
                </li>
                <li>
                  To comply with applicable laws, payment processor rules,
                  infrastructure provider requirements, and our Terms of
                  Service.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                5. Payments and Billing
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami may use third-party payment processors, such as
                Stripe, to process payments, subscriptions, invoices, refunds,
                taxes, billing records, and related transactions.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We do not store full credit card numbers on our own servers.
                Payment information is handled by the payment processor according
                to its own security and privacy practices. We may receive and
                store limited billing-related information, such as customer ID,
                subscription status, payment status, plan type, transaction
                history, and the last four digits or brand of a payment method
                when provided by the payment processor.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                6. Cookies and Similar Technologies
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may use cookies, local storage, session storage, analytics
                tools, and similar technologies to keep users signed in, remember
                preferences, protect against abuse, understand usage, and improve
                the service.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Your browser may allow you to block or delete cookies. Some
                features may not work correctly if cookies or local storage are
                disabled.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                7. Third-Party Service Providers
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may use third-party providers to operate SubtitleTsunami,
                including hosting providers, cloud storage providers, database
                providers, payment processors, email providers, analytics tools,
                logging tools, video processing tools, transcription services,
                translation services, and security tools.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                These providers may process information only as needed to provide
                their services to SubtitleTsunami, support the functionality of
                the platform, comply with legal obligations, or protect against
                fraud, abuse, or security threats.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                8. How We Share Information
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We do not sell your personal information. We may share
                information in limited situations, including:
              </p>

              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-slate-300">
                <li>
                  With service providers that help us operate, secure, process,
                  host, store, analyze, or improve SubtitleTsunami.
                </li>
                <li>
                  With payment processors for billing, subscriptions, taxes,
                  fraud prevention, and payment compliance.
                </li>
                <li>
                  When required by law, subpoena, court order, legal process, or
                  government request.
                </li>
                <li>
                  When necessary to enforce our Terms of Service, investigate
                  misuse, respond to copyright complaints, prevent fraud, protect
                  security, or defend legal rights.
                </li>
                <li>
                  In connection with a business transfer, merger, acquisition,
                  financing, restructuring, or sale of assets.
                </li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                9. Data Retention
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We keep information for as long as reasonably necessary to
                provide the service, maintain accounts, process payments, comply
                with legal obligations, resolve disputes, enforce agreements,
                prevent abuse, and maintain security.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Uploaded videos, generated subtitles, processed video files,
                preview links, download links, and temporary processing files may
                be deleted automatically after a limited period. Some logs,
                processing records, billing records, account records, and rights
                confirmation records may be retained longer when needed for
                business, legal, security, tax, payment, or compliance purposes.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                10. Security
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We use reasonable administrative, technical, and organizational
                safeguards designed to protect information from unauthorized
                access, misuse, loss, disclosure, alteration, or destruction.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                No internet-based service, storage system, or transmission
                method is completely secure. You are responsible for protecting
                your account credentials and for using the service only from
                trusted devices and networks.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                11. Your Choices
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                Depending on your location and applicable law, you may have
                rights to access, correct, delete, export, or restrict certain
                personal information. You may also be able to cancel your account
                or subscription through available account tools or by contacting
                us.
              </p>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                To request help with privacy-related questions, contact us using
                the information at the bottom of this policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                12. Children’s Privacy
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami is not intended for children under 13. We do not
                knowingly collect personal information from children under 13. If
                you believe a child has provided personal information to
                SubtitleTsunami, contact us and we will review the issue.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                13. International Users
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                SubtitleTsunami may be operated from the United States and may
                use service providers located in the United States or other
                countries. By using the service, you understand that information
                may be transferred to, stored in, or processed in locations
                outside your state, province, or country.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                14. Changes to This Privacy Policy
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                We may update this Privacy Policy from time to time. When we
                make changes, we may update the effective date above. Continued
                use of SubtitleTsunami after changes are posted means you accept
                the updated Privacy Policy.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-black text-white">
                15. Contact
              </h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">
                For questions about this Privacy Policy, personal information,
                account data, uploaded files, billing records, or privacy
                requests, contact:
              </p>

              <div className="mt-4 rounded-2xl border border-cyan-300/15 bg-slate-950/60 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300/80">
                  Contact Email
                </p>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                  <p className="break-all text-sm font-extrabold text-cyan-100">
                    {PRIVACY_EMAIL}
                  </p>

                  <button
                    type="button"
                    onClick={handleCopyEmail}
                    className="inline-flex w-fit items-center justify-center rounded-xl border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-xs font-black uppercase tracking-[0.12em] text-cyan-100 transition hover:-translate-y-0.5 hover:border-cyan-200/60 hover:bg-cyan-300/15 hover:text-white"
                  >
                    {copiedEmail ? 'Copied' : 'Copy Email'}
                  </button>
                </div>
              </div>
            </section>
          </div>
        </section>
      </main>

      <KeywordFooter />
    </>
  );
};

export default Privacy;